import React, { useState, useEffect } from 'react';
import { adminSettingsAPI } from '../../services/api';

// Fixed Costs Section Component
const FixedCostsSection: React.FC = () => {
  const [costs, setCosts] = useState<any[]>([]);
  const [totals, setTotals] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingCost, setEditingCost] = useState<any>(null);
  const [formData, setFormData] = useState({
    name: '',
    category: 'hosting',
    provider: '',
    costType: 'monthly',
    amount: '',
    currency: 'USD',
    exchangeRate: '83',
    billingCycle: 'monthly',
    description: '',
    notes: '',
    yearlyPaymentMonth: ''
  });

  useEffect(() => {
    loadPlatformCosts();
  }, []);

  const loadPlatformCosts = async () => {
    try {
      setLoading(true);
      const response = await adminSettingsAPI.getPlatformCosts();
      setCosts(response.data.costs || []);
      setTotals(response.data.totals);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to load platform costs');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setError('');
      setSuccess('');
      
      const data: any = {
        ...formData,
        amount: parseFloat(formData.amount),
        exchangeRate: parseFloat(formData.exchangeRate)
      };
      
      // Set yearlyPaymentMonth for yearly or one-time costs
      if ((formData.costType === 'yearly' || formData.costType === 'one-time') && formData.yearlyPaymentMonth) {
        data.yearlyPaymentMonth = parseInt(formData.yearlyPaymentMonth);
      } else {
        data.yearlyPaymentMonth = undefined;
      }

      if (editingCost) {
        await adminSettingsAPI.updatePlatformCost(editingCost.id, data);
        setSuccess('Platform cost updated successfully!');
      } else {
        await adminSettingsAPI.upsertPlatformCost(data);
        setSuccess('Platform cost added successfully!');
      }
      
      setShowAddForm(false);
      setEditingCost(null);
      resetForm();
      loadPlatformCosts();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to save platform cost');
    }
  };

  const handleEdit = (cost: any) => {
    setEditingCost(cost);
    setFormData({
      name: cost.name,
      category: cost.category,
      provider: cost.provider,
      costType: cost.costType,
      amount: cost.amount.toString(),
      currency: cost.currency,
      exchangeRate: cost.exchangeRate?.toString() || '83',
      billingCycle: cost.billingCycle || 'monthly',
      description: cost.description || '',
      notes: cost.notes || '',
      yearlyPaymentMonth: cost.yearlyPaymentMonth?.toString() || ''
    });
    setShowAddForm(true);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this platform cost?')) {
      return;
    }
    
    try {
      await adminSettingsAPI.deletePlatformCost(id);
      setSuccess('Platform cost deleted successfully!');
      loadPlatformCosts();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to delete platform cost');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      category: 'hosting',
      provider: '',
      costType: 'monthly',
      amount: '',
      currency: 'USD',
      exchangeRate: '83',
      billingCycle: 'monthly',
      description: '',
      notes: '',
      yearlyPaymentMonth: ''
    });
    setEditingCost(null);
  };

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      hosting: 'bg-blue-100 text-blue-800',
      deployment: 'bg-green-100 text-green-800',
      domain: 'bg-purple-100 text-purple-800',
      database: 'bg-yellow-100 text-yellow-800',
      cdn: 'bg-pink-100 text-pink-800',
      monitoring: 'bg-red-100 text-red-800',
      storage: 'bg-indigo-100 text-indigo-800',
      other: 'bg-gray-100 text-gray-800'
    };
    return colors[category] || colors.other;
  };

  if (loading) {
    return <div className="text-center py-4">Loading platform costs...</div>;
  }

  return (
    <div className="space-y-6 mt-8">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-800">Fixed Costs</h2>
        <button
          onClick={() => {
            resetForm();
            setShowAddForm(!showAddForm);
          }}
          className="px-4 py-2 text-sm bg-black text-white rounded hover:bg-gray-800"
        >
          {showAddForm ? 'Cancel' : '+ Add Fixed Cost'}
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-md p-4">
          <p className="text-green-800">{success}</p>
        </div>
      )}

      {/* Fixed Costs Summary */}
      {totals && (
        <div className="p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border border-purple-200">
          <h3 className="text-lg font-semibold text-gray-800 mb-3">Fixed Costs Summary</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white p-3 rounded">
              <p className="text-xs text-gray-600 mb-1">Monthly Total</p>
              <p className="text-xl font-bold text-black">{totals.monthly.formatted}</p>
            </div>
            <div className="bg-white p-3 rounded">
              <p className="text-xs text-gray-600 mb-1">Yearly Total</p>
              <p className="text-xl font-bold text-black">{totals.yearly.formatted}</p>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Form */}
      {showAddForm && (
        <form onSubmit={handleSubmit} className="p-4 bg-white border border-gray-200 rounded-lg">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">
            {editingCost ? 'Edit Fixed Cost' : 'Add Fixed Cost'}
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Name *</label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                placeholder="e.g., Render Backend"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Provider *</label>
              <input
                type="text"
                required
                value={formData.provider}
                onChange={(e) => setFormData({ ...formData, provider: e.target.value })}
                className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                placeholder="e.g., Render, Vercel"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Category *</label>
              <select
                required
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
              >
                <option value="hosting">Hosting</option>
                <option value="deployment">Deployment</option>
                <option value="domain">Domain</option>
                <option value="database">Database</option>
                <option value="cdn">CDN</option>
                <option value="monitoring">Monitoring</option>
                <option value="storage">Storage</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Cost Type *</label>
              <select
                required
                value={formData.costType}
                onChange={(e) => setFormData({ ...formData, costType: e.target.value, yearlyPaymentMonth: (e.target.value === 'yearly' || e.target.value === 'one-time') ? formData.yearlyPaymentMonth : '' })}
                className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
              >
                <option value="monthly">Monthly</option>
                <option value="yearly">Yearly</option>
                <option value="one-time">One-time</option>
                <option value="usage-based">Usage-based</option>
              </select>
            </div>
            {(formData.costType === 'yearly' || formData.costType === 'one-time') && (
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Payment Month (0=Jan, 11=Dec)
                  {formData.costType === 'one-time' && <span className="text-gray-500"> - For yearly one-time payments</span>}
                </label>
                <select
                  value={formData.yearlyPaymentMonth}
                  onChange={(e) => setFormData({ ...formData, yearlyPaymentMonth: e.target.value })}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                >
                  <option value="">Select Month</option>
                  {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map((month, idx) => (
                    <option key={idx} value={idx}>{month}</option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Amount *</label>
              <input
                type="number"
                step="0.01"
                min="0"
                required
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                placeholder="0.00"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Currency</label>
              <select
                value={formData.currency}
                onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
              >
                <option value="USD">USD ($)</option>
                <option value="INR">INR (₹)</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Exchange Rate (USD to INR)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={formData.exchangeRate}
                onChange={(e) => setFormData({ ...formData, exchangeRate: e.target.value })}
                className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                placeholder="83"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                rows={2}
                placeholder="Optional description"
              />
            </div>
          </div>
          <div className="mt-4 flex gap-2">
            <button
              type="submit"
              className="px-4 py-2 text-sm bg-black text-white rounded hover:bg-gray-800"
            >
              {editingCost ? 'Update' : 'Add'} Cost
            </button>
            <button
              type="button"
              onClick={() => {
                setShowAddForm(false);
                resetForm();
              }}
              className="px-4 py-2 text-sm bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* Fixed Costs List */}
      <div className="space-y-4">
        {costs.map((cost) => (
          <div key={cost.id} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <h4 className="text-sm font-semibold text-gray-800">{cost.name}</h4>
                  <span className={`px-2 py-1 text-xs rounded ${getCategoryColor(cost.category)}`}>
                    {cost.category}
                  </span>
                  <span className="text-xs text-gray-500">{cost.provider}</span>
                </div>
                {cost.description && (
                  <p className="text-xs text-gray-600 mb-2">{cost.description}</p>
                )}
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <span className="text-gray-600">Monthly: </span>
                    <span className="font-semibold text-black">{cost.monthlyCost.formatted}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Yearly: </span>
                    <span className="font-semibold text-black">{cost.yearlyCost.formatted}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Type: </span>
                    <span className="font-semibold">{cost.costType}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Amount: </span>
                    <span className="font-semibold">{cost.amount} {cost.currency}</span>
                  </div>
            {((cost.costType === 'yearly' || cost.costType === 'one-time') && cost.yearlyPaymentMonth !== undefined) && (
              <div className="col-span-2">
                <span className="text-gray-600">Payment Month: </span>
                <span className="font-semibold">
                  {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][cost.yearlyPaymentMonth]}
                </span>
              </div>
            )}
                </div>
              </div>
              <div className="flex gap-2 ml-4">
                <button
                  onClick={() => handleEdit(cost)}
                  className="px-3 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(cost.id)}
                  className="px-3 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        ))}
        {costs.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            No platform costs added yet. Click "Add Platform Cost" to get started.
          </div>
        )}
      </div>

      <button
        onClick={loadPlatformCosts}
        className="mt-4 px-4 py-2 text-sm bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
      >
        Refresh Data
      </button>
    </div>
  );
};

export default FixedCostsSection;
